import { StrictMode } from 'react';
import ReactDOM from 'react-dom';
import App from './App.tsx';
import { ThemeProvider, applyThemeColors } from 'chat-interface-lib';
// Import chat-interface-lib component CSS (no theme defaults - ai-central provides its own)
import 'chat-interface-lib/styles/components';
import './index.css';
import './App.css';
// Initialize multi-agent-chat i18n and mark as initialized
import './i18n/i18n';
(window as any).__CHAT_I18N_INITIALIZED__ = true;
import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker path at module scope (before any imports)
const configurePDFWorker = () => {
  // Only configure if we're in a browser environment
  if (typeof window !== 'undefined' && typeof window.location !== 'undefined') {
    try {
      const basePath = '';
      pdfjsLib.GlobalWorkerOptions.workerSrc = `${basePath}/pdf.worker.mjs`;
      console.log('PDF.js worker configured with path:', `${basePath}/pdf.worker.mjs`);
    } catch (error) {
      console.warn('Failed to configure PDF worker path:', error);
      // Fallback to default path
      pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.mjs';
    }
  }
};

// Configure worker immediately
configurePDFWorker();

// Function to apply custom favicon from environment or runtime config
function applyCustomFavicon() {
  // Check runtime config first (for n8n node), then fall back to build-time env vars
  const runtimeConfig = (window as { __CHAT_THEME_CONFIG__?: Record<string, string> }).__CHAT_THEME_CONFIG__ || {};
  const customFavicon = runtimeConfig.VITE_CUSTOM_FAVICON || import.meta.env.VITE_CUSTOM_FAVICON;
  
  if (customFavicon && customFavicon.trim() !== '') {
    // Remove any existing favicon links
    const existingFavicons = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]');
    existingFavicons.forEach(link => link.remove());
    
    // Add the custom favicon
    const faviconLink = document.createElement('link');
    faviconLink.rel = 'icon';
    faviconLink.type = 'image/png';
    faviconLink.href = customFavicon;
    document.head.appendChild(faviconLink);
  }
}

// Function to initialize the app with runtime config
function initializeApp() {
  // Apply theme colors from environment variables and runtime config
  applyThemeColors();
  
  // Apply custom favicon from environment variables and runtime config
  applyCustomFavicon();
  
  // Add scoped root class to prevent CSS conflicts
  const rootElement = document.getElementById('root');
  if (rootElement) {
    rootElement.classList.add('multi-agent-chat-root');
  }
  
  // Add multi-agent-chat context class to document for theme synchronization
  document.documentElement.classList.add('multi-agent-chat-context');
  
  ReactDOM.render(
    <StrictMode>
      <ThemeProvider>
        <App />
      </ThemeProvider>
    </StrictMode>,
    rootElement
  );
}

// Check if runtime config is already available
if (document.readyState === 'loading') {
  // Document is still loading, wait for DOMContentLoaded
  document.addEventListener('DOMContentLoaded', initializeApp);
} else {
  // Document is already loaded, initialize immediately
  initializeApp();
}

// Also re-apply theme colors and favicon when runtime config becomes available
const checkRuntimeConfig = () => {
  const runtimeConfig = (window as { __CHAT_THEME_CONFIG__?: Record<string, string> }).__CHAT_THEME_CONFIG__;
  if (runtimeConfig) {
    applyThemeColors();
    applyCustomFavicon();
  }
};

// Check for runtime config periodically for a short time
let checkCount = 0;
const maxChecks = 10;
const checkInterval = setInterval(() => {
  checkCount++;
  checkRuntimeConfig();
  if (checkCount >= maxChecks) {
    clearInterval(checkInterval);
  }
}, 100);
